import { buildTicketCoverage, type CoverageTicket } from "@msuite/utils/plans";
import type { Enums } from "@msuite/utils/types";
import { useMemo } from "react";
import { useCapacityCalendarContext } from "../capacity-calendar";
import { buildCoverageGradient } from "../planner-view";
import { useStaffingIndex } from "../use-staffing-index";
import type { CapacityProjectList } from "./types";

/**
 * The gradient a row draws across the window when its tickets are closed.
 *
 * Both rows that need one — a project and a manager — ask the same question of the same two reads,
 * so the roll-up lives here rather than twice over. The reads are the board's own and keyed on the
 * window, so a row costs no request of its own however many there are.
 *
 * The work is memoised on the window and the projects, because a coverage bar is drawn on **every**
 * row whether it is open or not: unlike the ticket rows beneath them, these are not lazy, and
 * recomputing them all on an unrelated render is the one way this gets expensive. Who is staffed
 * comes off the board's shared index rather than a pass over the stretches per row, for the same
 * reason.
 *
 * @param projects - The projects standing under the row, each with the tickets the window reaches.
 * @param color - The row's own colour, derived from its name so it holds still across reloads.
 * @returns The CSS gradient, or null when no day under the row carries work.
 */
export function useTicketCoverage(projects: CapacityProjectList, color: Enums<"color">) {
	/** Context */
	const { controller } = useCapacityCalendarContext();

	/** Hooks */
	const { crewIdsByTicket } = useStaffingIndex();

	/** Computed */
	return useMemo(() => {
		const dates = controller.cells.map((cell) => cell.cellMoment.db());
		const tickets: CoverageTicket[] = [];
		for (const project of projects) {
			for (const ticket of project.tickets) {
				tickets.push({
					startDate: ticket.startDate,
					endDate: ticket.endDate,
					dateMode: ticket.dateMode as "date" | "week" | null,
					weekdays: project.weekdays,
					excludedDates: (ticket._workExclusions ?? []).map((exclusion) => exclusion.date),
					workStartDate: ticket.workStartDate,
					estimatedDays: ticket.estimatedDays,
					isStaffed: crewIdsByTicket.has(ticket.id),
				});
			}
		}
		return buildCoverageGradient(buildTicketCoverage(dates, tickets), color);
	}, [controller.cells, projects, crewIdsByTicket, color]);
}
